/**
 * IntercomHelper - Modular class for managing Intercom user context updates.
 *
 * This class provides a centralized way to update Intercom attributes when user context changes,
 * ensuring support always has current information about:
 * - Extension installation status and version
 * - Tax software and intake system
 * - User role
 * - Device/browser context (per conversation)
 *
 * Usage:
 *   const helper = new IntercomHelper();
 *   await helper.refreshExtensionStatus(); // After extension heartbeat
 *   await helper.refreshSettings(); // After tax software/intake system change
 *   await helper.refreshAll(); // Refresh all user context
 */

import axios from "axios";
import { AXIOS_CONFIG } from "@utils/api";
import { backend_url } from "@/constants";
import { Endpoints } from "@utils/api/endpoints";

/**
 * User context data structure from backend
 */
export interface UserContext {
  tax_prep_software?: string;
  intake_system?: string;
  extension_installed?: string;
  extension_version?: string;
  user_role?: string;
  [key: string]: any;
}

/**
 * IntercomHelper class for managing Intercom user context updates.
 */
export class IntercomHelper {
  private jwtToken: string | null = null;
  private jwtTokenExpiry: number = 0;
  private readonly JWT_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

  /**
   * Check if Intercom is initialized and available.
   */
  private isIntercomAvailable(): boolean {
    return (
      typeof window !== "undefined" && typeof window.Intercom !== "undefined"
    );
  }

  /**
   * Filter out undefined and null values from an object.
   * Intercom shows "Unknown" for null values, so we filter them out.
   * Empty strings, 0, and false are kept as they are valid values.
   */
  private filterUndefined(obj: Record<string, any>): Record<string, any> {
    const filtered: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined && value !== null) {
        filtered[key] = value;
      }
    }
    return filtered;
  }

  /**
   * Fetch Intercom JWT token from the backend.
   * Uses caching to avoid unnecessary API calls.
   *
   * @returns Promise<string | null> - JWT token or null if fetch failed
   */
  async fetchJWT(): Promise<string | null> {
    // Return cached token if still valid
    const now = Date.now();
    if (this.jwtToken && now < this.jwtTokenExpiry) {
      return this.jwtToken;
    }

    try {
      const response = await axios.get(
        `${backend_url}/${Endpoints.onboarding.intercomJwt}`,
        AXIOS_CONFIG
      );
      const token = response.data.content?.intercomJwt || null;

      if (token) {
        this.jwtToken = token;
        this.jwtTokenExpiry = now + this.JWT_CACHE_DURATION_MS;
      }

      return token;
    } catch (error) {
      console.error("[IntercomHelper] Failed to fetch JWT:", error);
      return null;
    }
  }

  /**
   * Fetch user context data from the backend.
   *
   * @returns Promise<UserContext | null> - User context data or null if fetch failed
   */
  async fetchUserContext(): Promise<UserContext | null> {
    try {
      const response = await axios.get(
        `${backend_url}/${Endpoints.onboarding.intercomContext}`,
        AXIOS_CONFIG
      );

      const context = response.data.content || null;
      if (context) {
        console.log("[IntercomHelper] User context retrieved:", context);
      } else {
        console.warn("[IntercomHelper] User context is empty or null");
      }

      return context;
    } catch (error) {
      console.error("[IntercomHelper] Failed to fetch user context:", error);
      if (axios.isAxiosError(error)) {
        console.error("[IntercomHelper] Error details:", {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });
      }
      return null;
    }
  }

  /**
   * Update Intercom attributes with JWT authentication.
   *
   * @param attributes - Attributes to update
   * @returns Promise<boolean> - True if update was successful
   */
  async updateAttributes(attributes: Record<string, any>): Promise<boolean> {
    if (!this.isIntercomAvailable()) {
      console.warn(
        "[IntercomHelper] Intercom not initialized, cannot update attributes"
      );
      return false;
    }

    // Filter out undefined/null values
    const filteredAttributes = this.filterUndefined(attributes);

    if (Object.keys(filteredAttributes).length === 0) {
      console.log("[IntercomHelper] No attributes to update after filtering");
      return false;
    }

    try {
      // Double-check Intercom is available (TypeScript guard)
      if (!this.isIntercomAvailable() || !window.Intercom) {
        console.warn("[IntercomHelper] Intercom not available during update");
        return false;
      }

      // Fetch and set JWT token for authenticated update
      const jwt = await this.fetchJWT();
      if (jwt) {
        window.Intercom("setUserJwt", jwt);
      }

      // Update attributes
      window.Intercom("update", filteredAttributes);
      console.log("[IntercomHelper] Updated attributes:", filteredAttributes);
      return true;
    } catch (error) {
      console.error("[IntercomHelper] Error updating attributes:", error);
      return false;
    }
  }

  /**
   * Refresh extension-related attributes in Intercom.
   * Call this after extension heartbeat or installation/update.
   *
   * @returns Promise<boolean> - True if update was successful
   */
  async refreshExtensionStatus(): Promise<boolean> {
    console.log("[IntercomHelper] Refreshing extension status...");
    const context = await this.fetchUserContext();

    if (!context) {
      return false;
    }

    const attributes = {
      extension_installed: context.extension_installed,
      extension_version: context.extension_version,
    };

    return await this.updateAttributes(attributes);
  }

  /**
   * Refresh tax software and intake system attributes in Intercom.
   * Call this after tax office settings are saved.
   *
   * @returns Promise<boolean> - True if update was successful
   */
  async refreshSettings(): Promise<boolean> {
    console.log("[IntercomHelper] Refreshing settings...");
    const context = await this.fetchUserContext();

    if (!context) {
      return false;
    }

    const attributes = {
      tax_prep_software: context.tax_prep_software,
      intake_system: context.intake_system,
    };

    return await this.updateAttributes(attributes);
  }

  /**
   * Refresh user role attribute in Intercom.
   * Call this when user role changes.
   *
   * @returns Promise<boolean> - True if update was successful
   */
  async refreshUserRole(): Promise<boolean> {
    console.log("[IntercomHelper] Refreshing user role...");
    const context = await this.fetchUserContext();

    if (!context) {
      return false;
    }

    const attributes = {
      user_role: context.user_role,
    };

    return await this.updateAttributes(attributes);
  }

  /**
   * Refresh all user context attributes in Intercom.
   * Call this when you want to ensure all attributes are up-to-date.
   *
   * @returns Promise<boolean> - True if update was successful
   */
  async refreshAll(): Promise<boolean> {
    console.log("[IntercomHelper] Refreshing all user context...");
    const context = await this.fetchUserContext();

    if (!context) {
      return false;
    }

    const attributes = {
      tax_prep_software: context.tax_prep_software,
      intake_system: context.intake_system,
      extension_installed: context.extension_installed,
      extension_version: context.extension_version,
      user_role: context.user_role,
    };

    return await this.updateAttributes(attributes);
  }

  /**
   * Update specific attributes directly without fetching from backend.
   * Useful when you already have the updated values.
   *
   * @param attributes - Attributes to update
   * @returns Promise<boolean> - True if update was successful
   */
  async updateAttributesDirect(
    attributes: Record<string, any>
  ): Promise<boolean> {
    return await this.updateAttributes(attributes);
  }

  /**
   * Clear JWT token cache.
   * Useful if you need to force a fresh token fetch.
   */
  clearJWTCache(): void {
    this.jwtToken = null;
    this.jwtTokenExpiry = 0;
  }
}

/**
 * Singleton instance for convenience.
 * You can also create new instances if needed.
 */
export const intercomHelper = new IntercomHelper();
